import { TimelineTrack } from '../../types';
import { DraggedItemSnapshot } from '../../stores/use-timeline-store';

/**
 * Calculates constrained delta track for multi-drag to prevent boundary violations
 */
export const calculateConstrainedDeltaTrack = (
  deltaTrack: number,
  selectedItemsSnapshot: DraggedItemSnapshot[],
  tracks: TimelineTrack[]
): number => {
  let constrainedDeltaTrack = deltaTrack;

  for (const snapshot of selectedItemsSnapshot) {
    const itemNewTrackIndex = snapshot.originalRow + deltaTrack;

    // Check bottom boundary constraint
    if (itemNewTrackIndex >= tracks.length) {
      const maxAllowedDelta = tracks.length - 1 - snapshot.originalRow;
      constrainedDeltaTrack = Math.min(constrainedDeltaTrack, maxAllowedDelta);
    }

    // Check top boundary constraint
    if (itemNewTrackIndex < 0) {
      const minAllowedDelta = -snapshot.originalRow;
      constrainedDeltaTrack = Math.max(constrainedDeltaTrack, minAllowedDelta);
    }
  }

  return constrainedDeltaTrack;
};

/**
 * Calculates constrained delta time for multi-drag to prevent boundary violations
 */
export const calculateConstrainedDeltaTime = (
  deltaTime: number,
  selectedItemsSnapshot: DraggedItemSnapshot[],
  primaryItemId: string,
  totalDuration: number
): number => {
  let constrainedDeltaTime = deltaTime;

  for (const snapshot of selectedItemsSnapshot) {
    if (snapshot.id !== primaryItemId) {
      const itemNewStart = snapshot.originalStart + deltaTime;
      const itemDuration = snapshot.originalDuration;

      // Check left boundary constraint
      if (itemNewStart < 0) {
        const leftConstraint = -snapshot.originalStart;
        constrainedDeltaTime = Math.max(constrainedDeltaTime, leftConstraint);
      }

      // Check right boundary constraint
      if (itemNewStart + itemDuration > totalDuration) {
        const rightConstraint = totalDuration - itemDuration - snapshot.originalStart;
        constrainedDeltaTime = Math.min(constrainedDeltaTime, rightConstraint);
      }
    }
  }

  return constrainedDeltaTime;
};

/**
 * Creates snapshots of all items that should be dragged
 */
export const createDraggedItemsSnapshot = (
  itemId: string,
  selectedItemIds: string[],
  tracks: TimelineTrack[]
): DraggedItemSnapshot[] => {
  const selectedItemsSnapshot: DraggedItemSnapshot[] = [];

  // Get all items that should be dragged (either just the clicked item or all selected items)
  const itemsToDrag = selectedItemIds.includes(itemId) && selectedItemIds.length > 1
    ? selectedItemIds
    : [itemId];

  const allItems = tracks.flatMap(track => track.items);

  for (const dragItemId of itemsToDrag) {
    const draggedItem = allItems.find(i => i.id === dragItemId);
    if (draggedItem) {
      const draggedItemTrackIndex = tracks.findIndex(track => track.id === draggedItem.trackId);
      if (draggedItemTrackIndex !== -1) {
        selectedItemsSnapshot.push({
          id: draggedItem.id,
          originalStart: draggedItem.start,
          originalDuration: draggedItem.end - draggedItem.start,
          originalRow: draggedItemTrackIndex,
          type: draggedItem.type,
          label: draggedItem.label,
          data: draggedItem.data,
          mediaStart: draggedItem.mediaStart,
          mediaSrcDuration: draggedItem.mediaSrcDuration,
        });
      }
    }
  }

  return selectedItemsSnapshot;
};
